/**
 * Administration screens, grouped. Used by the Administration hub and the
 * sidebar so both always match. Every item is HR-level: the role needs
 * `module` (view, or modify when action: "modify") with COMPANY scope.
 */
export const ADMIN_SECTIONS = [
  {
    key: "organization",
    title: "Organization",
    items: [
      {
        to: "/admin/companies",
        label: "Companies",
        icon: "bi-buildings",
        color: "#0f766e",
        module: "company-setup",
        description:
          "Company tree: subsidiaries, details, addresses and branches.",
      },
      {
        to: "/branch",
        label: "Branches",
        icon: "bi-diagram-2",
        color: "#0ea5e9",
        module: "company-setup",
        description:
          "Branch tree, and each branch's check-in location and radius.",
      },
      {
        to: "/department",
        label: "Departments",
        icon: "bi-building",
        color: "#6366f1",
        module: "company-setup",
        description: "Departments per company.",
      },
      {
        to: "/job-title",
        label: "Job Titles",
        icon: "bi-person-badge",
        color: "#8b5cf6",
        module: "company-setup",
        description: "Job titles and their categories.",
      },
      {
        to: "/client",
        label: "Payroll Groups",
        icon: "bi-cash-coin",
        color: "#10b981",
        module: "company-setup",
        description: "Client codes used for payroll grouping.",
      },
      {
        to: "/admin/roles-permissions",
        label: "Roles & Permissions",
        icon: "bi-shield-lock",
        color: "#ef4444",
        module: "company-setup",
        description: "What each role can view or change, and in which scope.",
        match: (p) => p.startsWith("/admin/roles-permissions") || p === "/role",
      },
      {
        to: "/admin/employee-id-format",
        label: "Employee ID Format",
        icon: "bi-123",
        color: "#f59e0b",
        module: "company-setup",
        description: "Prefix, year and numbering of new employee IDs.",
      },
    ],
  },
  {
    key: "people",
    title: "People & Access",
    items: [
      {
        to: "/users",
        label: "User Accounts",
        icon: "bi-person-gear",
        color: "#0ea5e9",
        module: "users",
        description: "Logins, roles and account status.",
      },
      {
        to: "/admin/approval-rules",
        label: "Approval Setup",
        icon: "bi-signpost-split",
        color: "#10b981",
        module: "company-setup",
        description:
          "Who approves DTR, overtime, leave and check-in validation: company rules and a test box.",
      },
      {
        to: "/admin/announcements",
        label: "Announcements",
        icon: "bi-megaphone",
        color: "#ef4444",
        module: "announcements",
        description: "Post and schedule announcements for the dashboards.",
      },
      {
        to: "/admin/holidays",
        label: "Holidays",
        icon: "bi-calendar-heart",
        color: "#8b5cf6",
        module: "holidays",
        description:
          "National and company holidays for the year; copy last year's.",
      },
      {
        to: "/admin/dtr-cutoffs",
        label: "DTR Cut-offs",
        icon: "bi-calendar-range",
        color: "#0ea5e9",
        module: "company-setup",
        description:
          "When pay periods start and which day weeks start on, per company.",
      },
      {
        to: "/admin/notification-settings",
        label: "Notification Settings",
        icon: "bi-bell",
        color: "#f59e0b",
        module: "company-setup",
        description:
          "Which notifications each company sends, and the probation alerts.",
      },
      {
        to: "/employee/approvers",
        label: "Per-employee Approvers",
        icon: "bi-diagram-3",
        color: "#6366f1",
        module: "employees",
        description:
          "Exceptions: employees who need their own approvers instead of the company rule.",
      },
    ],
  },
  {
    key: "data",
    title: "Data Tools",
    items: [
      {
        to: "/admin/export",
        label: "Export",
        icon: "bi-box-arrow-up",
        color: "#10b981",
        module: "data-tools",
        description:
          "Download employees, attendance, DTR, leave balances, assets or users as Excel/CSV.",
      },
      {
        to: "/admin/import",
        label: "Import",
        icon: "bi-box-arrow-in-down",
        color: "#0ea5e9",
        module: "data-tools",
        description:
          "Bring in data from Excel: one kind of record, or new employees with all their details. Templates, workbook columns and history are tabs inside.",
        match: (p) =>
          p === "/admin/import" ||
          p === "/admin/data-mapping" ||
          p === "/importLogs",
      },
      {
        to: "/employee/import",
        label: "Import New Employees",
        icon: "bi-people",
        color: "#8b5cf6",
        module: "employees",
        action: "modify",
        description:
          "Add many employees at once from one workbook with all their details.",
        match: (p) => p.startsWith("/employee/import"),
      },
      {
        to: "/sync-sched",
        label: "Sync Schedules",
        icon: "bi-arrow-repeat",
        color: "#14b8a6",
        module: "data-tools",
        description: "Scheduled syncs from S3 / Zoho.",
      },
      {
        to: "/storageConfig",
        label: "Storage Settings",
        icon: "bi-hdd-network",
        color: "#a855f7",
        module: "data-tools",
        description: "Where uploaded documents are stored.",
      },
    ],
  },
  {
    key: "system",
    title: "System",
    items: [
      {
        to: "/auditLogs",
        label: "Audit Log",
        icon: "bi-journal-text",
        color: "#334155",
        module: "audit-logs",
        branchScope: true, // branch roles see their branches' changes
        description: "Every change: who, when, old → new.",
      },
    ],
  },
];

/**
 * The sections and items this user may open (companyWide / can from
 * usePermissions). Items are company-wide unless marked branchScope.
 */
export const visibleAdminSections = (companyWide, can) =>
  ADMIN_SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) =>
      i.branchScope && can
        ? can(i.module, i.action || "view")
        : companyWide(i.module, i.action || "view"),
    ),
  })).filter((s) => s.items.length > 0);
